import { createFileRoute, useRouterState } from '@tanstack/react-router'
import { useLayoutEffect, useRef } from 'react'
import type { BoggleGameModeId } from '~/boggle/gameModes'
import { RunScreen } from '~/boggle/ui/run/RunScreen'
import { HomeScreen } from '~/boggle/ui/screens/HomeScreen'
import { readBoggleGamesAutoStartFromHistoryState } from '~/boggle/ui/storage/gamesRouteState'
import { useBoggleModeSession } from '~/boggle/ui/state/ModeSession'
import {
  isBoggleDebugSearchEnabled,
  readBoggleDebugSeed,
} from '~/boggle/ui/state/debugSearch'

const HOME_STANDARD_GAME_MODE_ID = 'race' as const satisfies BoggleGameModeId

export const Route = createFileRoute('/')({
  head: () => ({
    // meta: [
    //   { title: 'Patter — Daily word game' },
    // ],
  }),
  component: IndexRoute,
})

function IndexRoute() {
  const navigate = Route.useNavigate()
  const navigateFromIndex = Route.useNavigate()
  const {
    clearPreferredGameMode,
    clearRouteHostedGameMode,
    endStandardRun,
    isDevMode,
    isStandardRunActive,
    newStandardGame,
    persistGameModeFromGamesRoute,
    prepareChallengeRun,
    standardSeed,
    standardRunGameModeId,
    startStandardRun,
  } = useBoggleModeSession()
  const shouldAutoStartStandardRun = useRouterState({
    select: state => readBoggleGamesAutoStartFromHistoryState(state.location.state),
  })
  const routerSearch = useRouterState({
    select: state =>
      ((state.location as { search?: Record<string, unknown> }).search ?? {}) as Record<
        string,
        unknown
      >,
  })
  const autoStartConsumedRef = useRef(false)

  const gameModeId = isStandardRunActive
    ? (standardRunGameModeId ?? HOME_STANDARD_GAME_MODE_ID)
    : HOME_STANDARD_GAME_MODE_ID
  const shouldDebugAutoStartRun = isBoggleDebugSearchEnabled(routerSearch)
  const debugSeed = shouldDebugAutoStartRun ? readBoggleDebugSeed(routerSearch) : null

  useLayoutEffect(() => {
    autoStartConsumedRef.current = false
  }, [])

  /** When the hub is not running the standard game, treat it like other non‑game routes and drop hosted-mode + stored preference noise. */
  useLayoutEffect(() => {
    if (!isStandardRunActive) {
      clearRouteHostedGameMode()
      clearPreferredGameMode()
    }
  }, [
    clearPreferredGameMode,
    clearRouteHostedGameMode,
    isStandardRunActive,
  ])

  useLayoutEffect(() => {
    if (isStandardRunActive) {
      persistGameModeFromGamesRoute(gameModeId)
    }
  }, [gameModeId, isStandardRunActive, persistGameModeFromGamesRoute])

  useLayoutEffect(() => {
    if (
      isStandardRunActive ||
      (!shouldAutoStartStandardRun && !shouldDebugAutoStartRun) ||
      autoStartConsumedRef.current
    ) {
      return
    }
    autoStartConsumedRef.current = true
    startStandardRun(gameModeId, debugSeed ?? undefined)
    void navigateFromIndex({
      to: '.',
      replace: true,
      search: prev => prev,
      state: prev => ({
        ...(typeof prev === 'object' && prev !== null ? prev : {}),
        boggleAutoStartStandardRun: undefined,
      }),
    })
  }, [
    debugSeed,
    gameModeId,
    isStandardRunActive,
    navigateFromIndex,
    shouldAutoStartStandardRun,
    shouldDebugAutoStartRun,
    startStandardRun,
  ])

  const onExitStandardRun = () => {
    endStandardRun()
  }

  if (isStandardRunActive) {
    return (
      <RunScreen
        gameModeId={gameModeId}
        onExitRun={onExitStandardRun}
        onNewGame={newStandardGame}
        runKind="standard"
        seed={standardSeed}
      />
    )
  }

  return (
    <HomeScreen
      isDevMode={isDevMode}
      onOpenChallenge={() => {
        prepareChallengeRun()
        void navigate({ to: '/challenge' as never })
      }}
      onOpenPlayground={() => {
        void navigate({ to: '/dev/playground' })
      }}
      onStartHardRun={() => {
        startStandardRun('race-hard')
      }}
      onStartStandardRun={() => {
        startStandardRun(HOME_STANDARD_GAME_MODE_ID)
      }}
    />
  )
}
